import { useEnvContext } from "context/EnvContext";
import { ASSESSMENT_SLUG, SEARCH_TYPE_CARE } from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  AccessDrainages as AccessDrainagesType,
  Auction,
  BaseCare as BaseCareType,
  Breathing as BreathingType,
  Companionship as CompanionshipType,
  CompressionCare as CompressionType,
  Dialysis as DialysisType,
  Food as FoodType,
  HelpAtHome as HelpAtHomeType,
  IncontinenceHelp as IncontinenceType,
  Injection as InjectionType,
  Medication as MedicationType,
  Palliative as PalliativeType,
  PatientNeedMonitoring,
  PatientNeedSupport,
  PsychiatricCare as PsychiatricCareType,
  WoundCare as WoundType,
} from "core/types";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { PatientInfoSlugContext } from ".";
import {
  BooleanField,
  Categories,
  CategoryDescription,
  EmptiableCategory,
  EmptiableField,
  PatientProfileInfoCard,
  StringField,
} from "./shared";
import {
  ComponentMappingItem,
  descriptionCategory,
  exists,
  Filter,
  getCategories,
  stringFilled,
} from "./utils";

function Nutrition({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: FoodType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="nutrition"
      empty={empty}
      title={translations.patient.careNeeds.nutrition}
    >
      <EmptiableField
        title={translations.patient.careNeeds.parenteralNutrition.label}
        testId="parenteral_nutrition"
        value={value?.parenteral_nutrition}
      />
      <EmptiableField
        title={translations.patient.careNeeds.enteralNutrition.label}
        testId="enteral_nutrition"
        value={value?.enteral_nutrition}
      />
    </EmptiableCategory>
  );
}

function Incontinence({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: IncontinenceType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="incontinence"
      empty={empty}
      title={translations.patient.careNeeds.incontinence}
    >
      <EmptiableField
        title={translations.patient.careNeeds.urinaryIncontinence}
        testId="urinary_incontinence"
        value={value?.urinary_incontinence}
      />
      <EmptiableField
        title={translations.patient.careNeeds.fecalIncontinence}
        testId="fecal_incontinence"
        value={value?.fecal_incontinence}
      />
    </EmptiableCategory>
  );
}

function Breathing({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: BreathingType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="breathing"
      empty={empty}
      title={translations.patient.careNeeds.breathing}
    >
      <EmptiableField
        title={translations.patient.careNeeds.tracheostomy.label}
        testId="tracheostomy"
        value={value?.tracheostomy}
      />
      <BooleanField
        value={value?.specialised_intensive_nurse}
        label={translations.ontologies.service.values.specialisedIntensiveNurse}
      />
      <EmptiableField
        title={translations.patient.careNeeds.oxygenTherapy.label}
        testId="oxygen_therapy"
        value={value?.oxygen_therapy}
      />
    </EmptiableCategory>
  );
}

function Dialysis({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: DialysisType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="dialysis"
      empty={empty}
      title={translations.patient.careNeeds.dialysis}
    >
      <EmptiableField
        title={translations.patient.careNeeds.hemodialysis.label}
        testId="hemodialysis"
        value={value?.hemodialysis}
      />
      <EmptiableField
        title={translations.patient.careNeeds.peritonealDialysis.label}
        testId="peritoneal_dialysis"
        value={value?.peritoneal_dialysis}
      />
    </EmptiableCategory>
  );
}

function Wounds({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: WoundType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="wound_care"
      empty={empty}
      title={translations.patient.careNeeds.woundCare}
    >
      <StringField testId="woundCare_description" value={value?.description} />
    </EmptiableCategory>
  );
}

function PsychiatricCare({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: PsychiatricCareType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="psychiatric_care"
      empty={empty}
      title={translations.patient.careNeeds.psychiatric_care.label}
    >
      <StringField
        testId="psychiatric_care_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function Medication({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: MedicationType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="medication"
      empty={empty}
      title={translations.patient.careNeeds.medication}
    >
      <StringField testId="medication_description" value={value?.description} />
      <EmptiableField
        testId="blood_thinner"
        title={translations.patient.takesBloodThinner}
        value={value?.blood_thinner}
      />
    </EmptiableCategory>
  );
}

function Injection({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: InjectionType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="injection"
      empty={empty}
      title={translations.patient.careNeeds.injection}
    >
      <StringField testId="injection_description" value={value?.description} />
    </EmptiableCategory>
  );
}

function AccessDrainages({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: AccessDrainagesType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="access_and_drainages"
      empty={empty}
      title={translations.patient.careNeeds.accessDrainages.accessAndDrainages}
    >
      <EmptiableField
        title={translations.patient.careNeeds.accessDrainages.trachealCannula}
        value={value?.tracheal_cannula}
        testId="tracheal_cannula"
      />
      <EmptiableField
        title={translations.patient.careNeeds.accessDrainages.gastricTube}
        value={value?.gastric_tube}
        testId="gastric_tube"
      />
      <EmptiableField
        title={
          translations.patient.careNeeds.accessDrainages.centralVenousCatheter
        }
        value={value?.central_venous_catheter}
        testId="central_venous_catheter"
      />
      <EmptiableField
        title={
          translations.patient.careNeeds.accessDrainages
            .peripheralVenousCatheter
        }
        value={value?.peripheral_venous_catheter}
        testId="peripheral_venous_catheter"
      />
      <EmptiableField
        title={
          translations.patient.careNeeds.accessDrainages.suprapubicCatheter
        }
        value={value?.suprapubic_catheter}
        testId="suprapubic_catheter"
      />
      <EmptiableField
        title={translations.patient.careNeeds.accessDrainages.permanentCatheter}
        value={value?.permanent_catheter}
        testId="permanent_catheter"
      />
      <EmptiableField
        title={translations.patient.careNeeds.accessDrainages.drainage}
        value={value?.drainage}
        testId="drainage"
      />
      <EmptiableField
        title={translations.patient.intravenousInjection}
        value={value?.intravenous_injection}
        testId="intravenous_injection"
      />
      <EmptiableField
        title={translations.patient.careNeeds.accessDrainages.other}
        value={value?.other_access_drainages}
        testId="other_access_drainages"
      />
    </EmptiableCategory>
  );
}

function Remarks({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: string | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="remarks"
      empty={empty}
      title={translations.patient.careNeeds.remarks}
    >
      <StringField value={value} testId="remarks" />
    </EmptiableCategory>
  );
}

function Compression({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: CompressionType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="compression"
      empty={empty}
      title={translations.patient.careNeeds.compression}
    >
      <StringField
        testId="compression_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}
function PalliativeCare({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: PalliativeType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="palliative_care"
      empty={empty}
      title={translations.patient.palliativeCare.label}
    >
      <CategoryDescription value={value?.description} />
      <BooleanField
        value={value?.palliative_certificate_needed}
        label={
          translations.patient.medicalDiagnosis.palliativeCertificateNeeded
        }
      />
    </EmptiableCategory>
  );
}

function BaseCare({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: BaseCareType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="base_care"
      empty={empty}
      title={translations.patient.careNeeds.baseCare}
    >
      <CategoryDescription value={value?.description} />
      <StringField
        testId="companionship_grant"
        value={
          value?.companionship_grant
            ? translations.patient.careNeeds.carePaidByHealthInsurance
            : ""
        }
      />
    </EmptiableCategory>
  );
}

function BasicMedicalCare({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="medical_care"
      empty={empty}
      title={translations.patient.careNeeds.medicalCare}
    >
      <StringField
        testId="medical_care_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function StaticCareForDisabledPatients({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: { description: string | null | undefined } | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="static_care_disabled_patients"
      empty={empty}
      title={translations.ontologies.service.values.staticCareDisabledPatients}
    >
      <StringField
        testId="disabled_patients_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function ImportantInformation({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: string | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="Important_information"
      empty={empty}
      title={translations.patient.careNeeds.titleImportantInformationCare}
    >
      <StringField value={value} testId="important_info" />
    </EmptiableCategory>
  );
}

function Companionship({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: CompanionshipType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="companionship"
      empty={empty}
      title={translations.patient.careNeeds.companionship}
    >
      <CategoryDescription value={value?.description} />
      <StringField
        testId="companionship_grant"
        value={
          value?.companionship_grant
            ? translations.patient.careNeeds.companionshipGrant
            : ""
        }
      />
    </EmptiableCategory>
  );
}

function HelpAtHome({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: HelpAtHomeType | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="help_at_home"
      empty={empty}
      title={translations.patient.careNeeds.helpAtHome}
    >
      <StringField
        testId="help_at_home_description"
        value={value?.description}
      />
    </EmptiableCategory>
  );
}

function Support({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: PatientNeedSupport | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="need_support"
      empty={empty}
      title={translations.patient.careNeeds.needSupport}
    >
      <BooleanField
        value={value?.eating}
        label={translations.patient.careNeeds.withEating}
      />
      <BooleanField
        value={value?.body_care}
        label={translations.patient.careNeeds.withBodyCare}
      />
      <BooleanField
        value={value?.dressing}
        label={translations.patient.careNeeds.withDressing}
      />
    </EmptiableCategory>
  );
}

function Monitoring({
  empty,
  translations,
  value,
}: {
  empty: boolean;
  translations: Translations;
  value: PatientNeedMonitoring | null | undefined;
}) {
  return (
    <EmptiableCategory
      testId="monitoring"
      empty={empty}
      title={translations.patient.careNeeds.monitoring}
    >
      <EmptiableField
        prefix={translations.patient.careNeeds.circulation.label}
        value={value?.circulation}
        testId="circulation"
      />
      <EmptiableField
        prefix={translations.patient.careNeeds.respiration.label}
        value={value?.respiration}
        testId="respiration"
      />
      <EmptiableField
        prefix={translations.patient.careNeeds.metabolism.label}
        value={value?.metabolism}
        testId="metabolism"
      />
    </EmptiableCategory>
  );
}

export const careneedsFilters: Record<string, Filter> = {
  palliative_care: {
    valueGetter: (auction) => auction?.patient?.care_needs?.palliative,
    exists: (auction) => auction?.patient?.care_needs?.palliative != null,
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.palliative != null &&
      (auction?.patient?.care_needs.palliative.description != null ||
        auction?.patient?.care_needs.palliative.palliative_certificate_needed !=
          null),
  },
  nutrition: {
    valueGetter: (auction) => auction?.patient?.care_needs?.nutrition,
    exists: (auction) => !!auction?.patient?.care_needs?.nutrition,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.nutrition &&
      ((auction?.patient?.care_needs.nutrition &&
        stringFilled(
          auction?.patient?.care_needs.nutrition.parenteral_nutrition,
        )) ||
        (auction?.patient?.care_needs.nutrition &&
          stringFilled(
            auction?.patient?.care_needs.nutrition.enteral_nutrition,
          ))) === true,
  },
  incontinence: {
    valueGetter: (auction) => auction?.patient?.care_needs?.incontinence,
    exists: (auction) => !!auction?.patient?.care_needs?.incontinence,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.incontinence &&
      (stringFilled(
        auction?.patient?.care_needs.incontinence.urinary_incontinence,
      ) ||
        (auction?.patient?.care_needs.incontinence &&
          stringFilled(
            auction?.patient?.care_needs.incontinence.fecal_incontinence,
          ))) === true,
  },
  breathing: {
    valueGetter: (auction) => auction?.patient?.care_needs?.breathing,
    exists: (auction) => !!auction?.patient?.care_needs?.breathing,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.breathing &&
      (stringFilled(auction?.patient?.care_needs.breathing.tracheostomy) ||
        auction?.patient?.care_needs.breathing?.specialised_intensive_nurse ||
        (auction?.patient?.care_needs.breathing &&
          stringFilled(
            auction?.patient?.care_needs.breathing.oxygen_therapy,
          ))) === true,
  },
  Dialysis: {
    valueGetter: (auction) => auction?.patient?.care_needs?.dialysis,
    exists: (auction) => !!auction?.patient?.care_needs?.dialysis,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.dialysis &&
      (stringFilled(
        auction?.patient?.care_needs.dialysis.peritoneal_dialysis,
      ) || stringFilled(auction?.patient?.care_needs.dialysis.hemodialysis)) ===
        true,
  },
  psychiatric_care: {
    valueGetter: (auction) => auction?.patient?.care_needs?.psychiatric_care,
    exists: (auction) => !!auction?.patient?.care_needs?.psychiatric_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.psychiatric_care),
  },
  Wounds: {
    valueGetter: (auction) => auction?.patient?.care_needs?.wound_care,
    exists: (auction) => !!auction?.patient?.care_needs?.wound_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.wound_care),
  },
  medication: {
    valueGetter: (auction) => auction?.patient?.care_needs?.medication,
    exists: (auction) => !!auction?.patient?.care_needs?.medication,
    notEmpty: (auction) =>
      (auction?.patient?.care_needs &&
        descriptionCategory(auction?.patient?.care_needs.medication)) ||
      auction?.patient?.care_needs?.medication?.blood_thinner !== null,
  },

  compression: {
    valueGetter: (auction) => auction?.patient?.care_needs?.compression,
    exists: (auction) => !!auction?.patient?.care_needs?.compression,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.compression),
  },
  injection: {
    valueGetter: (auction) => auction?.patient?.care_needs?.injection,
    exists: (auction) => !!auction?.patient?.care_needs?.injection,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.injection),
  },
  access_drainages: {
    valueGetter: (auction) => auction?.patient?.care_needs?.access_drainages,
    exists: (auction) => !!auction?.patient?.care_needs?.access_drainages,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.access_drainages &&
      (auction?.patient?.care_needs.access_drainages.tracheal_cannula != null ||
        auction?.patient?.care_needs.access_drainages.gastric_tube != null ||
        auction?.patient?.care_needs.access_drainages.central_venous_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages
          .peripheral_venous_catheter != null ||
        auction?.patient?.care_needs.access_drainages.suprapubic_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages.permanent_catheter !=
          null ||
        auction?.patient?.care_needs.access_drainages.drainage != null ||
        auction?.patient?.care_needs.access_drainages.intravenous_injection !=
          null ||
        auction?.patient?.care_needs.access_drainages.other_access_drainages !=
          null),
  },
  base_care: {
    valueGetter: (auction) => auction?.patient?.care_needs?.base_care,
    exists: (auction) => !!auction?.patient?.care_needs?.base_care,
    notEmpty: (auction) =>
      (auction?.patient?.care_needs?.base_care &&
        (stringFilled(auction?.patient?.care_needs.base_care.description) ||
          auction?.patient?.care_needs.base_care?.companionship_grant)) ===
      true,
  },

  important_information: {
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.important_information,
    exists: (auction) => !!auction?.patient?.care_needs?.important_information,
    notEmpty: (auction) =>
      stringFilled(auction?.patient?.care_needs?.important_information) ===
      true,
  },

  basic_medical_care: {
    valueGetter: (auction) => auction?.patient?.care_needs?.basic_medical_care,
    exists: (auction) => !!auction?.patient?.care_needs?.basic_medical_care,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.basic_medical_care?.description,
  },

  static_care_disabled_patients: {
    valueGetter: (auction) =>
      auction?.patient?.care_needs?.static_care_disabled_patients,
    exists: (auction) =>
      !!auction?.patient?.care_needs?.static_care_disabled_patients,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs?.static_care_disabled_patients
        ?.description,
  },

  companionship: {
    valueGetter: (auction) => auction?.patient?.care_needs?.companionship,
    exists: (auction) => !!auction?.patient?.care_needs?.companionship,
    notEmpty: (auction) =>
      (auction?.patient?.care_needs?.companionship &&
        (stringFilled(auction?.patient?.care_needs.companionship.description) ||
          auction?.patient?.care_needs.companionship?.companionship_grant)) ===
      true,
  },
  help_at_home: {
    valueGetter: (auction) => auction?.patient?.care_needs?.help_at_home,
    exists: (auction) => !!auction?.patient?.care_needs?.help_at_home,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      descriptionCategory(auction?.patient?.care_needs.help_at_home),
  },
  remarks: {
    valueGetter: (auction) => auction?.patient?.care_needs?.remarks,
    exists: (auction) =>
      auction?.patient?.care_needs?.remarks != null &&
      auction?.patient?.care_needs.remarks != "",
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.remarks != null &&
      auction?.patient?.care_needs.remarks != "",
  },
  support: {
    valueGetter: (auction) => auction?.patient?.care_needs?.support,
    exists: (auction) => auction?.patient?.care_needs?.support != null,
    notEmpty: (auction) =>
      auction?.patient?.care_needs?.support != null &&
      !!(
        auction?.patient?.care_needs.support.eating ||
        auction?.patient?.care_needs.support.body_care ||
        auction?.patient?.care_needs.support.dressing
      ),
  },
  monitoring: {
    valueGetter: (auction) => auction?.patient?.care_needs?.monitoring,
    exists: (auction) => auction?.patient?.care_needs?.monitoring != null,
    notEmpty: (auction) =>
      !!auction?.patient?.care_needs &&
      ((auction?.patient?.care_needs.monitoring != null &&
        stringFilled(auction?.patient?.care_needs.monitoring.circulation)) ||
        (auction?.patient?.care_needs.monitoring != null &&
          stringFilled(auction?.patient?.care_needs.monitoring.respiration)) ||
        (auction?.patient?.care_needs.monitoring != null &&
          stringFilled(auction?.patient?.care_needs.monitoring.metabolism))),
  },
};

const careneedsComponentMapping: ComponentMappingItem[] = [
  {
    Component: Support,
    key: "support",
  },
  {
    Component: PalliativeCare,
    key: "palliative_care",
  },
  {
    Component: PsychiatricCare,
    key: "psychiatric_care",
  },
  {
    Component: Nutrition,
    key: "nutrition",
  },
  {
    Component: Incontinence,
    key: "incontinence",
  },
  {
    Component: Breathing,
    key: "breathing",
  },
  {
    Component: Dialysis,
    key: "Dialysis",
  },
  {
    Component: Wounds,
    key: "Wounds",
  },
  {
    Component: Medication,
    key: "medication",
  },
  {
    Component: Monitoring,
    key: "monitoring",
  },
  {
    Component: Compression,
    key: "compression",
  },
  {
    Component: Injection,
    key: "injection",
  },
  {
    Component: AccessDrainages,
    key: "access_drainages",
  },
  {
    Component: ImportantInformation,
    key: "important_information",
  },

  {
    Component: BaseCare,
    key: "base_care",
  },
  {
    Component: BasicMedicalCare,
    key: "basic_medical_care",
  },
  {
    Component: StaticCareForDisabledPatients,
    key: "static_care_disabled_patients",
  },
  {
    Component: Companionship,
    key: "companionship",
  },
  {
    Component: HelpAtHome,
    key: "help_at_home",
  },
  {
    Component: Remarks,
    key: "remarks",
  },
];

export function CareNeedsCard({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const { isSenderApp } = useEnvContext();
  const translations = useTranslations();

  if (!auction.patient.care_needs) return null;

  const categories = getCategories({
    auction,
    componentMapping: careneedsComponentMapping,
    filters: careneedsFilters,
    forClinic: isSenderApp,
  });
  if (!exists(categories)) return null;

  return (
    <PatientInfoSlugContext.Provider
      value={{
        patientId: auction.patient.id,
        auctionId: auction.id,
        assessmentSlug: ASSESSMENT_SLUG.NEEDS,
      }}
    >
      <PatientProfileInfoCard
        title={
          auction.search_type === SEARCH_TYPE_CARE
            ? translations.patient.careNeeds.stepperTitleCare
            : translations.patient.careNeeds.stepperTitle
        }
      >
        <Categories
          getOntology={getOntology}
          translations={translations}
          categories={categories}
          auction={auction}
        />
      </PatientProfileInfoCard>
    </PatientInfoSlugContext.Provider>
  );
}
